/**
 * Tokenization and light stemming.
 *
 * The matcher compares what the user said against corpus tags, and those two will almost never
 * agree on word form: someone says "работаю", the tag says "работа"; someone says "cats", the
 * tag says "cat". Full morphology would need a library and a dictionary; crude suffix trimming
 * gets most of the way there for a two-language vocabulary of a few hundred topic words, and it
 * is small enough to be read and tested line by line.
 *
 * The rule throughout: trim only when the stem that remains is still long enough to be a word.
 * Over-trimming collapses unrelated words together, which is worse than missing a match.
 */

import type { Lang } from './types.js';

/** Words too common to carry any topic, in either language. */
const STOPWORDS: Record<Lang, ReadonlySet<string>> = {
  ru: new Set([
    'а', 'бы', 'в', 'вот', 'все', 'всё', 'да', 'для', 'до', 'его', 'ее', 'её', 'если', 'есть',
    'еще', 'ещё', 'же', 'за', 'и', 'из', 'или', 'им', 'их', 'к', 'как', 'ко', 'когда', 'кто',
    'ли', 'меня', 'мне', 'мы', 'на', 'над', 'не', 'нет', 'но', 'ну', 'о', 'об', 'он', 'она',
    'они', 'от', 'по', 'под', 'после', 'при', 'про', 'с', 'со', 'так', 'такой', 'там', 'тебя',
    'то', 'тоже', 'ты', 'у', 'уже', 'что', 'чтобы', 'это', 'этот', 'я',
    // Same reasoning as the English list: these look like content words and are not.
    'быть', 'вас', 'вам', 'весь', 'вы', 'мой', 'моя', 'моё', 'мочь', 'надо', 'нам', 'нас',
    'наш', 'него', 'неё', 'них', 'один', 'опять', 'очень', 'себя', 'сам', 'свой', 'сейчас',
    'совсем', 'тут', 'хотеть', 'ходить',
  ]),
  en: new Set([
    'a', 'about', 'all', 'am', 'an', 'and', 'any', 'are', 'as', 'at', 'be', 'been', 'but', 'by',
    'can', 'did', 'do', 'does', 'for', 'from', 'get', 'got', 'had', 'has', 'have', 'he', 'her',
    'him', 'his', 'how', 'i', 'if', 'in', 'is', 'it', 'its', 'just', 'me', 'my', 'no', 'not',
    'of', 'on', 'or', 'our', 'out', 'she', 'so', 'some', 'that', 'the', 'their', 'them', 'then',
    'there', 'they', 'this', 'to', 'too', 'up', 'us', 'was', 'we', 'were', 'what', 'when',
    'where', 'which', 'who', 'why', 'will', 'with', 'you', 'your',
    // Function-ish verbs and intensifiers. They feel like content words but carry no topic,
    // and left in they hijack matches: "I need more coffee" matched "a friend in need".
    'am', 'again', 'always', 'been', 'before', 'being', 'could', 'every', 'feel', 'go', 'going',
    'had', 'here', 'into', 'know', 'left', 'like', 'lot', 'made', 'make', 'more', 'most', 'much',
    'must', 'need', 'never', 'now', 'really', 'right', 'said', 'say', 'see', 'should', 'still',
    'take', 'tell', 'than', 'thing', 'things', 'think', 'very', 'want', 'way', 'well', 'went',
    'would', 'yet',
  ]),
};

/**
 * Russian inflectional endings, longest first so that `-ами` is tried before `-ми`.
 * Trimming stops at the first match, which is why order matters.
 */
const RU_SUFFIXES = [
  'иями', 'ями', 'ами', 'иях', 'ях', 'ах', 'ией', 'ией', 'ого', 'его', 'ому', 'ему', 'ыми',
  'ими', 'ешь', 'ете', 'ишь', 'ите', 'ают', 'яют', 'уют', 'юют', 'ать', 'ять', 'еть', 'ить',
  'ость', 'ов', 'ев', 'ей', 'ий', 'ый', 'ой', 'ая', 'яя', 'ое', 'ее', 'ые', 'ие', 'ам', 'ям',
  'ом', 'ем', 'ах', 'ух', 'ют', 'ут', 'ат', 'ят', 'ла', 'ло', 'ли', 'ть', 'а', 'я', 'о', 'е',
  'ы', 'и', 'у', 'ю', 'й', 'ь',
];

/** English endings. Deliberately fewer — English inflects far less. */
const EN_SUFFIXES = ['iness', 'ingly', 'ings', 'ing', 'ies', 'ied', 'ers', 'er', 'es', 'ed', 's'];

/** A stem shorter than this is meaningless, so trimming never goes below it. */
const MIN_STEM = 3;

/** One pass of suffix trimming. Returns the input unchanged when nothing applies. */
function trimOnce(word: string, lang: Lang): string {
  const suffixes = lang === 'ru' ? RU_SUFFIXES : EN_SUFFIXES;
  for (const suffix of suffixes) {
    if (word.length - suffix.length >= MIN_STEM && word.endsWith(suffix)) {
      // `ies` -> `y` keeps "stories" and "story" together; every other case just drops.
      if (lang === 'en' && suffix === 'ies') return `${word.slice(0, -3)}y`;
      return word.slice(0, -suffix.length);
    }
  }
  return word;
}

/**
 * Reduces one word to a comparable stem.
 *
 * Trimming runs **twice**, because Russian stacks endings: `работаю` sheds `ю` to become
 * `работа`, which is itself the nominative and must shed `а` to reach the stem `работ` that
 * `работе` and `работы` also reach. One pass leaves those forms in two different buckets, which
 * is exactly the bug the tests caught.
 *
 * Two passes, not a loop: a loop would keep eating short words down to the three-letter floor
 * and start colliding unrelated stems.
 */
export function stem(word: string, lang: Lang): string {
  return trimOnce(trimOnce(word.toLowerCase(), lang), lang);
}

/**
 * Splits text into stemmed, de-duplicated content words.
 *
 * Punctuation, digits and stop-words are dropped. Order is preserved, because the UI shows the
 * matched terms back to the user and they read better in the order they were said.
 */
export function tokenize(text: string, lang: Lang): string[] {
  const words = text
    .toLowerCase()
    .replace(/[^\p{L}\p{N}\s-]/gu, ' ')
    .split(/[\s-]+/)
    .filter((w) => w.length > 0);

  const stops = STOPWORDS[lang];
  const seen = new Set<string>();
  const out: string[] = [];

  for (const word of words) {
    if (stops.has(word)) continue;
    if (word.length < 2) continue;
    const stemmed = stem(word, lang);
    if (stemmed.length < 2 || seen.has(stemmed)) continue;
    seen.add(stemmed);
    out.push(stemmed);
  }

  return out;
}

/** True when a word carries no topic on its own. Exported for corpus linting. */
export function isStopword(word: string, lang: Lang): boolean {
  return STOPWORDS[lang].has(word.toLowerCase());
}
